import { motion } from 'framer-motion';
import type { IconType } from 'react-icons';

import type { Award } from '../data/products';
import OptimizedImage from './OptimizedImage';

export interface StoreLink {
  name: string;
  url: string;
  icon?: IconType;
  iconText?: string;
}

export interface FeaturedProductProps {
  title: string;
  subtitle?: string;
  description: string;
  imageUrl: string;
  category?: string;
  features: string[];
  ctaText: string;
  ctaLink?: string;
  ctaOnClick?: () => void;
  storeLinks?: StoreLink[];
  reversed?: boolean;
  award?: Award;
}

const FeaturedProduct = ({
  title,
  subtitle,
  description,
  imageUrl,
  category,
  features,
  ctaText,
  ctaLink,
  ctaOnClick,
  storeLinks,
  reversed = false,
  award
}: FeaturedProductProps) => {
  const isExternalLink = ctaLink?.startsWith('http') ?? false;

  return (
    <div className="py-4">
      <div className={`flex flex-col ${reversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-6 lg:gap-8 items-center`}>
        {/* Image */}
        <motion.div
          className="w-full lg:w-1/2"
          initial={{ opacity: 0, x: reversed ? -20 : 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="rounded-xl overflow-hidden shadow-surface bg-card dark:bg-panel aspect-[4/3] flex items-center justify-center relative">
            {imageUrl ? (
              <OptimizedImage
                src={imageUrl}
                alt={title}
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            ) : (
              <div className="w-full h-full bg-card dark:bg-panel/80 flex items-center justify-center">
                <span className="text-theme-secondary">Product Image</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          className="w-full lg:w-1/2"
          initial={{ opacity: 0, x: reversed ? 20 : -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {category && (
            <div className="inline-block bg-secondary/20 text-secondary px-3 py-1 rounded-full text-sm font-medium mb-3">
              {category}
            </div>
          )}
          <h3 className="text-2xl md:text-3xl font-bold text-primary dark:text-theme-primary mb-3">
            {title}
          </h3>
          {subtitle && (
            <p className="text-lg text-theme-secondary font-medium mb-4">
              {subtitle}
            </p>
          )}
          <div className="text-theme-secondary mb-6">
            {description.split('\n\n').map((paragraph, index) => (
              <p key={index} className="mb-4 last:mb-0">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Text-based Achievement Display */}
          {award && (
            <div className="mb-6 space-y-2">
              <a
                href="https://www.bookmarketingprofits.com/PinnacleAwardsFall2025.html"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm font-semibold text-brand-secondary hover:text-brand-primary transition-colors duration-200"
              >
                Pinnacle Book Achievement Award Winner
              </a>
              <a
                href="https://www.amazon.com/Konnect-Better-Unlocking-Relationships-Connections/dp/B0FV14D5V1"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm font-semibold text-brand-secondary hover:text-brand-primary transition-colors duration-200"
              >
              Amazon Best Seller
              </a>
            </div>
          )}

          {/* Features */}
          {features.length > 0 && (
            <ul className="space-y-3 mb-6">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <svg
                    className="w-5 h-5 text-secondary mt-1 mr-2 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-theme-secondary">{feature}</span>
                </li>
              ))}
            </ul>
          )}

          {/* CTA Button - Supports both link and onClick */}
          {ctaText && (ctaLink || ctaOnClick) && (
            ctaOnClick ? (
              <motion.button
                onClick={ctaOnClick}
                className="btn-primary inline-flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {ctaText}
              </motion.button>
            ) : (
              <motion.a
                href={ctaLink}
                target={isExternalLink ? '_blank' : undefined}
                rel={isExternalLink ? 'noopener noreferrer' : undefined}
                className="btn-primary inline-block"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {ctaText}
              </motion.a>
            )
          )}

          {/* Store Links */}
          {storeLinks && storeLinks.length > 0 && (
            <div className="mt-6">
              <p className="text-sm text-theme-secondary mb-3">Also available at:</p>
              <div className="flex flex-wrap gap-3">
                {storeLinks.map((store) => (
                  <motion.a
                    key={store.name}
                    href={store.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-12 h-12 rounded-full bg-card dark:bg-panel border border-border hover:border-secondary hover:bg-secondary/10 transition-all duration-200"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    title={store.name}
                  >
                    {store.icon ? (
                      <store.icon className="w-6 h-6 text-primary dark:text-theme-primary" />
                    ) : (
                      <span className="text-xs font-bold text-primary dark:text-theme-primary">
                        {store.iconText}
                      </span>
                    )}
                  </motion.a>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default FeaturedProduct;
